import { useEffect, useState } from "react";
import { resolveLocalImageUrl } from "../../data/local-assets";

interface Props {
  src?: string | null;
  alt?: string;
  className?: string;
  fallbackLabel?: string;
}

export function GameImage({
  src,
  alt = "",
  className = "challenge-image",
  fallbackLabel = "?",
}: Props) {
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  // When a Fluent 3D webp is missing, fall back to the legacy OpenMoji svg
  // (same emoji codepoint) before giving up to the text label.
  const [useOpenmoji, setUseOpenmoji] = useState(false);

  useEffect(() => {
    setFailed(!src);
    setRetry(0);
    setUseOpenmoji(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div className={`${className} challenge-image-fallback`} aria-label={alt || "Missing picture"}>
        {fallbackLabel}
      </div>
    );
  }

  const fluentMatch = src.match(/\/images\/fluent\/([0-9a-f-]+)\.webp$/i);
  const effectiveSrc = useOpenmoji && fluentMatch?.[1]
    ? `/images/openmoji/${fluentMatch[1].toUpperCase()}.svg`
    : src;

  const localSrc = resolveLocalImageUrl(effectiveSrc) ?? effectiveSrc;
  const resolvedSrc = retry > 0 && localSrc.startsWith("/")
    ? `${localSrc}${localSrc.includes("?") ? "&" : "?"}retry=${retry}`
    : localSrc;

  return (
    <img
      key={`${effectiveSrc}:${retry}`}
      className={className}
      src={resolvedSrc}
      alt={alt}
      loading="eager"
      decoding="async"
      onError={() => {
        if (retry < 2) {
          window.setTimeout(() => setRetry((n) => n + 1), 180);
          return;
        }
        if (!useOpenmoji && fluentMatch) {
          // Fluent webp missing — switch to the OpenMoji svg and retry.
          setUseOpenmoji(true);
          setRetry(0);
          return;
        }
        setFailed(true);
      }}
    />
  );
}
